import React, { useState, useEffect } from 'react';
import { useSafeState } from '../hooks/useSafeState';

const FlipCounter = ({ count = 0, isLoading = false }) => {
  const [displayCount, setDisplayCount] = useSafeState(count);
  const [animatingDigits, setAnimatingDigits] = useSafeState(new Set());
  // Per-digit version key — increments on each change so React unmounts the
  // old digit and the new one mounts with the flip-in keyframe.
  const [digitVersions, setDigitVersions] = useSafeState({});

  useEffect(() => {
    if (count !== displayCount && !isLoading) {
      const oldFormatted = formatNumber(displayCount);
      const newFormatted = formatNumber(count);
      const oldDigits = oldFormatted.split('');
      const newDigits = newFormatted.split('');
      const maxLength = Math.max(oldDigits.length, newDigits.length);

      // Pad with spaces for comparison
      const paddedOld = oldDigits.concat(Array(maxLength - oldDigits.length).fill(' '));
      const paddedNew = newDigits.concat(Array(maxLength - newDigits.length).fill(' '));

      // Find which digits are changing
      const changingPositions = new Set();
      paddedOld.forEach((digit, index) => {
        if (digit !== paddedNew[index]) {
          changingPositions.add(index);
        }
      });

      setAnimatingDigits(changingPositions);

      // Update display after a brief delay for animation
      setTimeout(() => {
        setDisplayCount(count);
        setAnimatingDigits(new Set());
        // Bump version for each changing position so the new digit mounts fresh
        // and the flip-in keyframe runs. Keys include this version so React
        // doesn't reuse the previous DOM node.
        setDigitVersions((prev) => {
          const next = { ...prev };
          changingPositions.forEach((pos) => {
            next[pos] = (next[pos] || 0) + 1;
          });
          return next;
        });
      }, 150);
    }
  }, [count, displayCount, isLoading]);

  const formatNumber = (num) => {
    try {
      return (num || 0).toLocaleString();
    } catch (error) {
      console.error('Error formatting number:', error);
      return String(num || 0);
    }
  };

  const formattedNumber = formatNumber(displayCount);
  const digits = formattedNumber.split('');

  // Don't render anything if still loading and no count
  if (isLoading && count === null) {
    return null;
  }

  return (
    <div className="flex items-center justify-center py-2">
      {/* Component-scoped delight: digits roll upward when they change.
          Lives here (not in index.css) because the typeset agent owns that file.
          280ms is just under our 300ms ceiling; strong ease-out feels like a flip
          settling, not a slide. */}
      <style>{`
        @keyframes flip-counter-roll-in {
          0%   { transform: translateY(0.5em); opacity: 0; }
          60%  { opacity: 1; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .flip-counter-rolling {
          animation: flip-counter-roll-in 280ms cubic-bezier(0.23, 1, 0.32, 1) both;
        }
        @media (prefers-reduced-motion: reduce) {
          .flip-counter-rolling { animation: none; }
        }
      `}</style>
      <div className="flex items-center text-sm text-gray-900 dark:text-gray-100 rounded-full px-3 py-1 bg-background/40 backdrop-blur-md border border-foreground/10">
        <div className="flex items-center">
          {digits.map((digit, index) => {
            const isAnimating = animatingDigits.has(index);
            const version = digitVersions[index] || 0;
            return (
              <span
                key={`${index}-${digit}-${version}`}
                className="font-inter font-light flip-counter-outer inline-block"
                style={{ overflow: 'hidden' }}
              >
                <span
                  className={`flip-counter-digit inline-block ${
                    isAnimating
                      ? 'transform -translate-y-1 opacity-50'
                      : 'transform translate-y-0 opacity-100 flip-counter-rolling'
                  }`}
                >
                  {digit}
                </span>
              </span>
            );
          })}
          <span className="ml-1 text-xs">layers. Zero regrets.</span>
        </div>
      </div>
    </div>
  );
};

export default FlipCounter;